import { Link } from 'react-router-dom';
import BlogPostLayout from '../components/BlogPostLayout';
import type { ContentsItem } from '../components/TableOfContents';
import { Helmet } from "react-helmet-async";

const PACKING_CHECKLIST_CONTENTS: ContentsItem[] = [
  { level: 'h2', text: 'Before you start packing', id: 'before-start' },
  { level: 'h2', text: 'Clothing checklist', id: 'clothing' },
  { level: 'h2', text: 'Personal care items', id: 'personal-care' },
  { level: 'h2', text: 'Making your room feel like home', id: 'room-feel-like-home' },
  { level: 'h2', text: 'Practical essentials', id: 'practical-essentials' },
  { level: 'h2', text: 'Important: Label everything', id: 'label-everything' },
  { level: 'h2', text: 'Your first day bag', id: 'first-day-bag' },
  { level: 'h2', text: 'Moving day tips', id: 'moving-day-tips' },
  { level: 'h2', text: 'Adjusting after you\'ve moved in', id: 'adjusting' },
  { level: 'h2', text: 'Getting help with your move', id: 'getting-help' },
];

function WhatToPackMovingCareHomeChecklist() {
  return (
    <>
    <Helmet>
        <title> What to Pack When Moving into a Care Home: A Checklist </title>
        <meta
          name="description"
          content="A practical checklist to help make the move into a care home smoother, covering everything from personal items and clothes to furniture."
        />
        <link rel="canonical" href="https://www.harpwood.house/help-and-advice/post/what-to-pack-moving-care-home-checklist" />
      </Helmet>

    <BlogPostLayout title="What to Pack When Moving into a Care Home: A Checklist" contents={PACKING_CHECKLIST_CONTENTS}>
      <div className="blog-post-content">
        <p>Moving into a care home means bringing the things that matter most whilst leaving behind what you no longer need. The items you choose help create a space that feels genuinely yours from day one. This practical guide breaks down what to pack into simple categories, making it easier to prepare for your move.</p>

        <h2 id="before-start">Before you start packing</h2>
        <p>Every care home has slightly different policies about what residents can bring, so it's worth checking a few things first:</p>
        <ul>
          <li><strong>Room size and layout</strong> – knowing dimensions helps you understand what will fit</li>
          <li><strong>Whether you can bring your own furniture</strong> (armchairs, small tables, etc.)</li>
          <li><strong>Electrical item policies</strong> for televisions, radios, or other appliances</li>
          <li><strong>Labelling requirements</strong> for clothing and belongings</li>
        </ul>
        <p><Link to="/contact-us" className="nurse-care-link">Getting in touch with us</Link> before packing allows you to ask specific questions about what works best in your room.</p>
        <blockquote>
          <p>"The residents who settle most quickly are often those who've brought meaningful personal items from day one. Photographs, favourite cushions, a beloved armchair – these things help a room feel like home rather than just a space." – Harpwood House</p>
        </blockquote>

        <h2 id="clothing">Clothing checklist</h2>

        <p><strong>Must haves</strong></p>
        <p><em>Everyday essentials:</em></p>
        <ul>
          <li>7–10 comfortable tops</li>
          <li>7–10 pairs of trousers, skirts, or other bottoms</li>
          <li>2–3 cardigans or jumpers</li>
          <li>7 sets of nightwear</li>
          <li>Dressing gown and slippers (with backs and non-slip soles)</li>
          <li>10–14 pairs of underwear</li>
          <li>7–10 pairs of socks or tights</li>
        </ul>

        <p><em>Footwear:</em></p>
        <ul>
          <li>2 pairs of comfortable, supportive shoes with non-slip soles</li>
          <li>1 pair of slippers with backs and grips</li>
          <li>1 pair of outdoor shoes or boots</li>
        </ul>

        <p><em>Practical tips:</em> Choose front-fastening items if dexterity is limited, elasticated waistbands for comfort, and machine-washable fabrics. Avoid anything too fiddly or requiring special care.</p>

        <p><strong>Nice to haves</strong></p>
        <ul>
          <li>1–2 smarter outfits for family events or celebrations</li>
          <li>Light jacket or coat for outdoor time</li>
          <li>Hat and scarf for colder weather</li>
          <li>Something special you feel confident wearing for photographs</li>
        </ul>

        <p><strong>Don't need</strong></p>
        <ul>
          <li>Excessive amounts of formal clothing</li>
          <li>Shoes that need breaking in or have slippery soles</li>
          <li>Clothes requiring dry cleaning or complex care</li>
          <li>More than 2–3 coats or heavy jackets</li>
        </ul>

        <h2 id="personal-care">Personal care items</h2>

        <p><strong>Must haves</strong></p>
        <ul>
          <li>Preferred soap, shower gel, or body wash</li>
          <li>Shampoo and conditioner</li>
          <li>Toothbrush and toothpaste (or denture care products)</li>
          <li>Deodorant</li>
          <li>Moisturiser</li>
          <li>Hairbrush and comb</li>
          <li>Glasses (plus spare pair if possible)</li>
          <li>Hearing aids (with spare batteries if you use them)</li>
        </ul>

        <p><strong>Nice to haves</strong></p>
        <ul>
          <li>Makeup or grooming items you use regularly</li>
          <li>Perfume or aftershave</li>
          <li>Hair styling products</li>
          <li>Electric razor or shaving equipment</li>
          <li>Nail care items</li>
        </ul>

        <p><strong>Don't need</strong></p>
        <ul>
          <li>Large bottles of products (bring smaller sizes initially)</li>
          <li>Multiple backup bottles of everything</li>
          <li>Products you rarely use</li>
        </ul>

        <h2 id="room-feel-like-home">Making your room feel like home</h2>

        <p><strong>Must haves</strong></p>
        <p><em>Photographs and memories:</em></p>
        <ul>
          <li>Multiple framed family photographs</li>
          <li>Pictures from important life events</li>
          <li>Photo albums you enjoy looking through</li>
        </ul>

        <p><em>Basic comfort:</em></p>
        <ul>
          <li>Favourite cushions or throw</li>
          <li>Familiar bedding if you prefer (duvet, pillows, bedspread)</li>
          <li>Clock for your room</li>
          <li>Calendar</li>
        </ul>

        <p><strong>Nice to haves</strong></p>
        <p><em>Personal touches:</em></p>
        <ul>
          <li>Favourite pictures, paintings, or prints</li>
          <li>Small ornaments or collectibles</li>
          <li>Plants (real or artificial)</li>
          <li>Small lamp or lampshade</li>
          <li>Religious or spiritual items important to you</li>
          <li>Favourite armchair (if space allows and we've confirmed it fits)</li>
        </ul>

        <p><em>Technology and entertainment:</em></p>
        <ul>
          <li>Television (Already provided at Harpwood House)</li>
          <li>Radio</li>
          <li>Tablet or laptop</li>
          <li>Mobile phone with charger</li>
          <li>Books you're reading or return to</li>
          <li>Craft supplies or hobby materials</li>
          <li>Playing cards or games</li>
        </ul>

        <p><strong>Don't need</strong></p>
        <ul>
          <li>Excessive furniture that overcrowds the room</li>
          <li>Large collections of ornaments or decorative items</li>
          <li>Multiple sets of bedding</li>
          <li>Too many electrical items</li>
        </ul>

        <h2 id="practical-essentials">Practical essentials</h2>

        <p><strong>Must haves</strong></p>
        <ul>
          <li>Hangers for wardrobe</li>
          <li>Laundry basket</li>
          <li>Tissues</li>
          <li>Sunglasses and hat for outdoor time</li>
          <li>Umbrella</li>
          <li>Writing materials (address book, notepaper, pens)</li>
          <li>Important documents (NHS card, medication list, contact details)</li>
        </ul>

        <p><strong>Nice to haves</strong></p>
        <ul>
          <li>Magnifying glass if you use one</li>
          <li>Spare batteries for hearing aids or devices</li>
          <li>Small torch</li>
          <li>Items for specific hobbies or interests</li>
        </ul>

        <p><strong>Don't need</strong></p>
        <ul>
          <li>Large amounts of cash</li>
          <li>Space heaters or unapproved electrical items</li>
          <li>Excessive storage containers</li>
        </ul>

        <h2 id="label-everything">Important: Label everything</h2>
        <p>All clothing and personal items should be clearly labelled with your name. Use permanent marker, iron-on labels, or sew-in name tapes. Many homes may provide their own labelling service but this is not a guarantee, so it's best to do so before arrival. This includes:</p>
        <ul>
          <li>Every item of clothing</li>
          <li>Shoes (inside and outside)</li>
          <li>Toiletries and personal care items</li>
          <li>Glasses cases and hearing aid cases</li>
          <li>Technology items and chargers</li>
        </ul>

        <h2 id="first-day-bag">Your first day bag</h2>
        <p>Pack a separate bag with immediate essentials for the first 24 hours:</p>
        <ul>
          <li>2 changes of comfortable clothing</li>
          <li>Toiletries and personal care items</li>
          <li>Medications (clearly labelled)</li>
          <li>Glasses and hearing aids</li>
          <li>Mobile phone and charger</li>
          <li>Pyjamas, dressing gown, and slippers</li>
          <li>Favourite photograph or small comfort item</li>
        </ul>
        <p>This ensures you have everything you need whilst settling in, even if other belongings arrive later.</p>

        <h2 id="moving-day-tips">Moving day tips</h2>
        <p>For a smoother transition:</p>
        <ul>
          <li>Bring photographs in frames ready to display</li>
          <li>Plan to spend time arranging your room on the first day</li>
          <li>Don't feel you need to bring everything immediately</li>
          <li>Family can bring additional items over the following days</li>
          <li>Give yourself time to adjust before filling every surface</li>
        </ul>

        <h2 id="adjusting">Adjusting after you've moved in</h2>
        <p>After a few weeks, you might find you need different items than you initially thought. That's completely normal. Family members can bring things you've realised you miss, or take home items you're not using. Your room should evolve to support the life you're actually living, not what you anticipated needing.</p>
        <blockquote>
          <p>"Your room becomes your private sanctuary, a place to rest and surround yourself with things that matter. Taking time to make it genuinely yours makes an enormous difference to how settled you feel." – Harpwood House</p>
        </blockquote>

        <h2 id="getting-help">Getting help with your move</h2>
        <p>If you have questions about what to bring, what's provided, or how to make your room feel like home, <Link to="/contact-us" className="nurse-care-link">getting in touch</Link> allows you to discuss these practical details before moving day.</p>

        <p>Moving into a care home is a significant change, but thoughtfully choosing what to bring helps maintain comfort, familiarity, and a sense of home throughout the transition.</p>
      </div>
    </BlogPostLayout>
    </>
  );
}

export default WhatToPackMovingCareHomeChecklist;
